import styled from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { COLORS } from '../../../../theme/tokens';
import { gradientRing } from '../../generating.styles';

/**
 * Every control that spends a model call wears the same gradient ring, and
 * nothing else in the editor does. That is the whole signal: an author scanning
 * the page can tell what asks Lumio to write from what edits what they wrote,
 * without reading a single label.
 *
 * `sm` sits in a panel head beside a section title, among 34px controls. `md` is
 * the app-wide 48px control height, so it lines up with the ordinary `Button` it
 * stands next to in a dialog footer.
 */
export const Root = styled.button<{ $size: 'sm' | 'md' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  height: ${(p) => (p.$size === 'sm' ? '34px' : '48px')};
  padding: ${(p) => (p.$size === 'sm' ? '0 12px' : '0 18px')};
  border-radius: 99px;
  font-family: inherit;
  font-size: ${(p) => (p.$size === 'sm' ? '13px' : '16px')};
  font-weight: 600;
  white-space: nowrap;
  color: ${COLORS.textStrong};
  cursor: pointer;
  ${gradientRing(COLORS.surfaceCard)}

  &:hover:not(:disabled) {
    --gen-surface: ${COLORS.surfaceMuted};
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

/** The assistant's mark, sized to the label beside it. */
export const Mark = styled.img<{ $size: 'sm' | 'md' }>`
  width: ${(p) => (p.$size === 'sm' ? '15px' : '17px')};
  height: ${(p) => (p.$size === 'sm' ? '15px' : '17px')};
  display: block;
  flex: none;
`;

/* The inline credit cost, after a soft divider — the same "cost reads on the
   action itself" pattern the creatives GenerateButton set, sized down to this
   pill (the Lumio mark already leads the label, so the cost group carries no
   second mark). */
export const CostGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const Sep = styled.span`
  opacity: 0.5;
  font-weight: 400;
`;

export const CostText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 0.92em;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  opacity: 0.85;
`;
